import { Student as GraphStudent } from "@/__generated__/types";
import { Student, DeepPartial } from "@/types/Student";

export function processStudent(node: DeepPartial<GraphStudent>): Student {
  return {
    ...node,
    imageUrl: {
      url: node.profilePhoto?.url || undefined,
    },
    studentOrganizationStatus: node.studentOrganizationStatus || undefined,
    majors: node?.majors?.edges
      ?.map((e) => ({ id: e?.node?.id, name: e?.node?.name }))
      .filter((mjr) => mjr !== undefined),
    interests: node?.interests?.edges
      ?.map((e) => ({ id: e?.node?.id, name: e?.node?.name }))
      .filter((intr) => intr !== undefined),
    socials: node?.socials?.edges
      ?.map((e) => ({
        handle: e?.node?.handle,
        platformName: e?.node?.platform?.name,
        baseUrl: e?.node?.platform?.baseUrl || undefined,
        iconUrl: e?.node?.platform?.icon?.url || undefined,
      }))
      .filter((social) => social.platformName !== undefined),
  };
}

export function getLastActiveLabel(lastActive: string | number): string {
  if (!lastActive || lastActive === "") {
    return "Never active";
  }
  const now = Date.now();
  let lastActiveTime: number;
  if (typeof lastActive === "number") {
    lastActiveTime =
      lastActive > 1000000000000 ? lastActive : lastActive * 1000;
  } else {
    lastActiveTime = new Date(lastActive).getTime();
  }

  if (isNaN(lastActiveTime)) {
    return "Invalid date";
  }
  const diff = now - lastActiveTime;
  if (diff < 0) {
    return "Active now";
  }

  const min = Math.floor(diff / 60000);
  const hr = Math.floor(min / 60);
  const days = Math.floor(hr / 24);
  const months = Math.floor(days / 30);

  if (months >= 1) {
    return "Last active over a month ago";
  }
  if (min < 60) {
    return `Last active ${min}m ago`;
  }
  if (hr < 24) {
    return `Last active ${hr}h ago`;
  }
  return `Last active ${days}d ago`;
}

export function isActive(lastActive: string | number): boolean {
  if (!lastActive) return false;
  let lastActiveTime: number;
  if (typeof lastActive === "number") {
    lastActiveTime =
      lastActive > 1000000000000 ? lastActive : lastActive * 1000;
  } else {
    lastActiveTime = new Date(lastActive).getTime();
  }

  const diff = Date.now() - lastActiveTime;
  return diff < 30 * 60 * 1000; // 30 minutes
}

export function getTopPercentileBadge(
  t: (key: string) => string,
  percentile?: number,
): string | null {
  if (!percentile) return null;
  if (percentile <= 1) return t("studentCard.top1");
  if (percentile <= 5) return t("studentCard.top5");
  if (percentile <= 10) return t("studentCard.top10");
  if (percentile <= 25) return t("studentCard.top25");
  return null;
}
